
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Search, X, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useState } from 'react';
import useRobustTranslation from '@/hooks/useRobustTranslation';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MobileMenu = ({ isOpen, onClose }: MobileMenuProps) => {
  const { getLocalizedPath, language, changeLanguageAndNavigate, languageNames, languageFlags } = useLanguage();
  const { nav } = useRobustTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [showAbout, setShowAbout] = useState(false);

  if (!isOpen) return null;

  const mainNavLinks = [
    { id: 'home', name: nav('home'), path: '/' },
    { id: 'findTherapist', name: nav('findTherapist'), path: '/find' },
    { id: 'specialties', name: nav('specialties'), path: '/specialties' },
    { id: 'howItWorks', name: nav('howItWorks'), path: '/how-it-works' },
    { id: 'forTherapists', name: nav('forTherapists'), path: '/for-therapists' },
    { id: 'pricing', name: nav('pricing'), path: '/pricing' },
  ];

  const aboutLinks = [
    { id: 'aboutUs', name: nav('about'), path: '/about' },
    { id: 'joseLuisMarin', name: 'Dr. José Luis Marín', path: '/jose-luis-marin' },
    { id: 'contact', name: 'Contacto', path: '/contact' },
  ];

  const handleLinkClick = () => {
    setShowAbout(false);
    onClose();
  };

  const isActiveLink = (path: string) => {
    return location.pathname === path || location.pathname === getLocalizedPath(path);
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 xl:hidden"
        onClick={onClose}
      />
      
      {/* Menu Panel */}
      <div className="fixed inset-y-0 right-0 w-80 max-w-[85vw] bg-white shadow-xl z-50 xl:hidden transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">{nav('menu')}</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-6">
              {/* Main Navigation */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wide">Navegación</h3>
                <div className="space-y-1">
                  {mainNavLinks.map((link) => (
                    <Link
                      key={link.id}
                      to={getLocalizedPath(link.path)}
                      onClick={handleLinkClick}
                      className={cn(
                        'block px-4 py-3 rounded-lg text-base font-medium transition-colors',
                        isActiveLink(link.path)
                          ? 'text-brand-blue-700 bg-brand-blue-50'
                          : 'text-gray-700 hover:text-brand-blue-600 hover:bg-gray-50'
                      )}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* About Section */}
              <div>
                <button
                  onClick={() => setShowAbout(!showAbout)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700"
                >
                  {nav('about')}
                  <ChevronRight className={cn("h-4 w-4 transition-transform", showAbout && "rotate-90")} />
                </button>
                {showAbout && (
                  <div className="space-y-1 mt-2">
                    {aboutLinks.map((link) => (
                      <Link
                        key={link.id}
                        to={getLocalizedPath(link.path)}
                        onClick={handleLinkClick}
                        className={cn(
                          'block px-6 py-3 rounded-lg text-base font-medium transition-colors',
                          isActiveLink(link.path)
                            ? 'text-brand-blue-700 bg-brand-blue-50'
                            : 'text-gray-700 hover:text-brand-blue-600 hover:bg-gray-50'
                        )}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Search Button */}
              <div className="border-t pt-6">
                <Button 
                  className="w-full justify-center gap-2 py-4 text-base"
                  onClick={onClose}
                >
                  <Search className="h-4 w-4" />
                  Buscar Psicoterapeuta
                </Button>
              </div>

              {/* Auth Section */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wide">Cuenta</h3>
                <div className="space-y-3">
                  {!user ? (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500 uppercase">Iniciar Sesión</p>
                        <div className="grid grid-cols-2 gap-2">
                          <Link 
                            to="/client-login"
                            onClick={handleLinkClick}
                            className="px-3 py-2.5 rounded-lg border border-gray-300 text-center text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            Cliente
                          </Link>
                          <Link 
                            to="/therapist-login"
                            onClick={handleLinkClick}
                            className="px-3 py-2.5 rounded-lg border border-gray-300 text-center text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            Terapeuta
                          </Link>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500 uppercase">Registrarse</p>
                        <div className="grid grid-cols-2 gap-2">
                          <Link 
                            to="/client-signup"
                            onClick={handleLinkClick}
                            className="px-3 py-2.5 rounded-lg bg-primary text-center text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                          >
                            Cliente
                          </Link>
                          <Link 
                            to="/therapist-signup"
                            onClick={handleLinkClick}
                            className="px-3 py-2.5 rounded-lg bg-primary text-center text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                          >
                            Terapeuta
                          </Link>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      <Link 
                        to="/client-portal"
                        onClick={handleLinkClick}
                        className="px-4 py-3 rounded-lg border border-gray-300 text-center text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Portal Cliente
                      </Link>
                      <Link 
                        to="/therapist-portal"
                        onClick={handleLinkClick}
                        className="px-4 py-3 rounded-lg border border-gray-300 text-center text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Portal Terapeuta
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Language Selection */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wide">Cambiar Idioma</h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(languageNames).map(([code, name]) => (
                    <Button 
                      key={code}
                      variant="ghost" 
                      className={cn(
                        "justify-start h-auto py-3 px-3",
                        language === code ? "bg-brand-blue-50 text-brand-blue-600" : "hover:bg-gray-50"
                      )}
                      onClick={() => {
                        changeLanguageAndNavigate(code as any);
                        onClose();
                      }}
                    >
                      <span className="mr-2 text-lg">{languageFlags[code as keyof typeof languageFlags]}</span>
                      <span className="text-sm">{name}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
